import React, { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import { Wallet, Zap, Lock } from 'lucide-react';
import { getBudgetten, ProjectMijlpaalCap } from '../../services/dataCockpitApi';
import { keurExtraUrenGoed } from '../../services/lopendeTrajectenApi';
import { usePeriode } from '../../store/dataPeriodeStore';
import { DataFilterBalk, DataKop, Kaart, KpiTegel, Laden, Fout } from '../../components/data/DataFilterBalk';
import { StackedBarChart, Legenda, Meter, KLEUR, euro } from '../../components/data/DataCharts';

const ExtraUren: React.FC<{ m: ProjectMijlpaalCap }> = ({ m }) => {
  const { t } = useTranslation('employer');
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [uren, setUren] = useState('4');
  const mut = useMutation({
    mutationFn: () => keurExtraUrenGoed(m.milestone_id, Number(uren)),
    onSuccess: () => {
      toast.success(t('dataCockpit.budget.extraUrenOk', { uren }));
      setOpen(false);
      qc.invalidateQueries({ queryKey: ['data-budgetten'] });
    },
    onError: (e: any) => toast.error(e?.response?.data?.message || t('dataCockpit.fout')),
  });
  if (!open) {
    return (
      <button type="button" onClick={() => setOpen(true)} className="inline-flex items-center gap-1.5 rounded-lg bg-[#0B1F3A] px-3 py-1.5 text-xs font-semibold text-white hover:bg-[#002466]">
        <Zap className="h-3.5 w-3.5" aria-hidden /> {t('dataCockpit.budget.keurExtraUren')}
      </button>
    );
  }
  return (
    <form
      className="flex items-center gap-2"
      onSubmit={(e) => {
        e.preventDefault();
        if (Number(uren) > 0) mut.mutate();
      }}
    >
      <input
        type="number"
        min={0.5}
        step={0.5}
        value={uren}
        onChange={(e) => setUren(e.target.value)}
        aria-label={t('dataCockpit.budget.extraUrenAantal')}
        className="w-20 rounded-lg border border-gray-300 px-2 py-1 text-sm"
      />
      <button type="submit" disabled={mut.isPending || !(Number(uren) > 0)} className="rounded-lg bg-[#0B1F3A] px-3 py-1.5 text-xs font-semibold text-white disabled:opacity-50">
        {t('dataCockpit.budget.bevestig')}
      </button>
      <button type="button" onClick={() => setOpen(false)} className="text-xs text-gray-500 underline">{t('dataCockpit.annuleren')}</button>
    </form>
  );
};

/** Submap 3 — Wallet- & Projectbudgetten (Financieel Regiecentrum). */
export const DataBudgettenPage: React.FC = () => {
  const { t } = useTranslation('employer');
  const { periode, key } = usePeriode();
  const q = useQuery({ queryKey: ['data-budgetten', ...key], queryFn: () => getBudgetten(periode) });
  const d = q.data;
  const reeksen = [
    { key: 'preventief', label: t('dataCockpit.budget.preventief'), kleur: KLEUR.blauw },
    { key: 'arbo', label: t('dataCockpit.budget.arbo'), kleur: KLEUR.rood },
    { key: 'crisis', label: t('dataCockpit.budget.crisis'), kleur: KLEUR.oranje },
  ];

  return (
    <div className="mx-auto max-w-7xl px-4 py-6 sm:px-6 lg:px-8">
      <DataKop titel={t('dataCockpit.budget.titel')} ondertitel={t('dataCockpit.budget.ondertitel')} icoon={<Wallet className="h-6 w-6" />} />
      <DataFilterBalk />
      {q.isLoading && <Laden />}
      {q.isError && <Fout fout={q.error} />}
      {d && (
        <div className="space-y-6">
          {/* Wallet- & POP-budgetmeter */}
          <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
            <KpiTegel label={t('dataCockpit.budget.gealloceerd')} waarde={euro(d.wallet.gealloceerd)} sub={d.wallet.bron ? t(`dataCockpit.budget.bron.${d.wallet.bron}`) : t('dataCockpit.budget.geenBudget')} />
            <KpiTegel label={t('dataCockpit.budget.geconsumeerd')} waarde={euro(d.wallet.geconsumeerd)} sub={t('dataCockpit.budget.boekingen', { n: d.wallet.boekingen })} />
            <KpiTegel label={t('dataCockpit.budget.resterend')} waarde={euro(d.wallet.resterend)} sub={t('dataCockpit.budget.medewerkers', { n: d.wallet.medewerkers })} />
            <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
              <p className="text-xs font-medium text-gray-500">{t('dataCockpit.budget.verbruik')}</p>
              <p className="mt-1 text-2xl font-bold text-gray-900">{d.wallet.verbruik_pct == null ? '—' : `${d.wallet.verbruik_pct.toLocaleString('nl-NL')}%`}</p>
              <div className="mt-2"><Meter waarde={d.wallet.geconsumeerd} max={d.wallet.gealloceerd ?? 0} label={t('dataCockpit.budget.verbruik')} /></div>
            </div>
          </div>

          {/* Gestapelde cashflow */}
          <Kaart titel={t('dataCockpit.budget.cashflow')} uitleg={t('dataCockpit.budget.cashflowUitleg')} actie={<Legenda items={reeksen.map((r) => ({ label: r.label, kleur: r.kleur }))} />}>
            {d.cashflow.maanden.every((m) => !m.preventief && !m.arbo && !m.crisis) ? (
              <p className="py-10 text-center text-sm text-gray-500">{t('dataCockpit.budget.geenFacturen')}</p>
            ) : (
              <StackedBarChart maanden={d.cashflow.maanden.map((m) => m.maand)} reeksen={reeksen} waarden={d.cashflow.maanden as unknown as Record<string, number>[]} />
            )}
            <div className="mt-3 grid grid-cols-1 gap-2 text-sm sm:grid-cols-3">
              {reeksen.map((r) => (
                <p key={r.key} className="flex items-center justify-between rounded-lg bg-gray-50 px-3 py-2">
                  <span className="flex items-center gap-2"><span className="h-2.5 w-2.5 rounded-sm" style={{ background: r.kleur }} />{r.label}</span>
                  <strong>{euro(d.cashflow.totalen[r.key as 'preventief' | 'arbo' | 'crisis'])}</strong>
                </p>
              ))}
            </div>
            {d.cashflow.buiten_grafiek > 0 && (
              <p className="mt-2 text-xs text-gray-500">{t('dataCockpit.budget.buitenGrafiek', { bedrag: euro(d.cashflow.buiten_grafiek) })}</p>
            )}
          </Kaart>

          {/* Harde Project Budget-Grendel */}
          <Kaart titel={t('dataCockpit.budget.grendel')} uitleg={t('dataCockpit.budget.grendelUitleg')}>
            {d.projecten.length === 0 ? (
              <p className="py-6 text-center text-sm text-gray-500">{t('dataCockpit.budget.geenProjecten')}</p>
            ) : (
              <ul className="divide-y divide-gray-100">
                {d.projecten.map((m) => (
                  <li key={m.milestone_id} className="flex flex-col gap-3 py-3 sm:flex-row sm:items-center">
                    <div className="min-w-0 flex-1">
                      <p className="flex items-center gap-2 text-sm font-medium text-gray-900">
                        {m.geblokkeerd && <Lock className="h-4 w-4 text-red-600" aria-label={t('dataCockpit.budget.geblokkeerd')} />}
                        {m.project} — {m.mijlpaal}
                      </p>
                      <p className="text-xs text-gray-500">{m.professional ?? '—'}</p>
                      <div className="mt-2 max-w-md">
                        <Meter waarde={m.geschreven_uren} max={m.budget_uren} kleur={m.geblokkeerd ? KLEUR.rood : KLEUR.blauw} label={m.mijlpaal} />
                        <p className="mt-1 text-xs text-gray-600">
                          {t('dataCockpit.budget.urenRegel', { geschreven: m.geschreven_uren.toLocaleString('nl-NL'), cap: m.budget_uren.toLocaleString('nl-NL') })}
                          {m.extra_uren_toegekend > 0 && ` · ${t('dataCockpit.budget.extraToegekend', { n: m.extra_uren_toegekend.toLocaleString('nl-NL') })}`}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      {m.geblokkeerd ? (
                        <span className="rounded-full bg-red-50 px-2 py-0.5 text-xs font-medium text-red-800">🔒 {t('dataCockpit.budget.capBereikt')}</span>
                      ) : (
                        <span className="rounded-full bg-green-50 px-2 py-0.5 text-xs font-medium text-green-800">{t('dataCockpit.budget.loopt')}</span>
                      )}
                      <ExtraUren m={m} />
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </Kaart>
        </div>
      )}
    </div>
  );
};

export default DataBudgettenPage;
